import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../../../supabaseClient';
import {
  Grid, TextField, Box, alpha, useTheme, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemButton, IconButton,
  Chip, Typography, Snackbar, Alert
} from '@mui/material';
import {
  Gavel as GavelIcon, SaveAs as SaveAsIcon, FolderOpen as FolderOpenIcon,
  Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon
} from '@mui/icons-material';
import { SectionPaper, SectionTitle } from './styledComponents';

const DEFAULT_TERMS = ``;

const TermsAndConditionsSection = ({ invoice, onUpdate, canEdit = true, canDelete = true }) => {
  const theme = useTheme();
  const { companyId } = useParams();
  const [templates, setTemplates] = useState([]);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { if (companyId) fetchTemplates(); }, [companyId]);

  useEffect(() => {
    if (!invoice.terms_and_conditions?.trim()) {
      onUpdate({ terms_and_conditions: DEFAULT_TERMS });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('terms_templates').select('*').eq('company_id', companyId)
        .in('template_type', ['invoice', 'both']).order('template_name');
      if (error) throw error;
      setTemplates(data || []);
    } catch (err) { console.error('Error fetching templates:', err); }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) { setSnackbar({ open: true, message: 'Please enter a template name', severity: 'error' }); return; }
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('terms_templates').insert([{
        company_id: companyId, template_name: templateName.trim(),
        template_type: 'invoice', terms_content: invoice.terms_and_conditions || '', created_by: user?.id
      }]);
      if (error) throw error;
      await fetchTemplates();
      setTemplateName('');
      setSaveDialogOpen(false);
      setSnackbar({ open: true, message: 'Template saved successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save template', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('terms_templates').update({
        template_name: editingTemplate.template_name, terms_content: editingTemplate.terms_content
      }).eq('id', editingTemplate.id);
      if (error) throw error;
      await fetchTemplates();
      setEditDialogOpen(false);
      setEditingTemplate(null);
      setSnackbar({ open: true, message: 'Template updated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update template', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    try {
      const { error } = await supabase.from('terms_templates').delete().eq('id', templateToDelete.id);
      if (error) throw error;
      await fetchTemplates();
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      setSnackbar({ open: true, message: 'Template deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete template', severity: 'error' });
    }
  };

  return (
    <>
      <SectionPaper sx={{ width: '100%', mt: 3 }}>
        <SectionTitle variant="h6"><GavelIcon />Terms & Conditions</SectionTitle>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
              {canEdit && (
                <Button size="small" startIcon={<FolderOpenIcon />} variant="outlined"
                  onClick={() => setLoadDialogOpen(true)}
                  sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white' }}>
                  Load Template
                </Button>
              )}
              {canEdit && (
                <>
                  <Button size="small" startIcon={<SaveAsIcon />} variant="outlined"
                    onClick={() => setSaveDialogOpen(true)}
                    disabled={!invoice.terms_and_conditions?.trim()}
                    sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white' }}>
                    Save as Template
                  </Button>
                </>
              )}
            </Box>

            <TextField multiline rows={40}
              value={invoice.terms_and_conditions || ''}
              onChange={(e) => canEdit && onUpdate({ terms_and_conditions: e.target.value })}
              fullWidth disabled={!canEdit}
              placeholder="Enter terms and conditions..."
              InputProps={{
                sx: {
                  borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white', opacity: canEdit ? 1 : 0.6,
                  fontFamily: 'monospace', fontSize: '0.85rem',
                  '& fieldset': { border: 'none' }
                }
              }}
            />
          </Grid>
        </Grid>
      </SectionPaper>

      {/* Load Template Dialog */}
      <Dialog open={loadDialogOpen} onClose={() => setLoadDialogOpen(false)} maxWidth="sm" fullWidth disableScrollLock>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 500, color: 'primary.main' }}>
          Load Template
          <IconButton onClick={() => setLoadDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {templates.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No templates saved yet.</Typography>
          ) : (
            <List>
              {templates.map((t) => (
                <ListItem key={t.id} disablePadding secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => { setEditingTemplate({ ...t }); setEditDialogOpen(true); setLoadDialogOpen(false); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {canDelete && (
                      <IconButton size="small" color="error" onClick={() => { setTemplateToDelete(t); setDeleteDialogOpen(true); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                }>
                  <ListItemButton onClick={() => { onUpdate({ terms_and_conditions: t.terms_content }); setLoadDialogOpen(false); }} sx={{ borderRadius: 2 }}>
                    <ListItemText
                      primary={t.template_name}
                      secondary={<Chip label={t.template_type} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLoadDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>Save as Template</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Template Name" value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g. Standard Invoice Terms" sx={{ mt: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveTemplate()} />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => { setSaveDialogOpen(false); setTemplateName(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTemplate} disabled={saving || !templateName.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={editDialogOpen} onClose={() => { setEditDialogOpen(false); setEditingTemplate(null); }} maxWidth="sm" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Template
          <IconButton onClick={() => { setEditDialogOpen(false); setEditingTemplate(null); }} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {editingTemplate && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField fullWidth label="Template Name" value={editingTemplate.template_name}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, template_name: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={8} label="Content" value={editingTemplate.terms_content}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, terms_content: e.target.value }))} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => { setEditDialogOpen(false); setEditingTemplate(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateTemplate} disabled={saving}>
            {saving ? 'Saving...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontWeight: 500, color: 'error.main' }}>Delete Template?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Are you sure you want to delete "{templateToDelete?.template_name}"?</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteTemplate}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TermsAndConditionsSection;
